import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { ListaPrecio } from '../types/listaPrecio.types';
import { ListaPrecioService } from '../services/listaPrecioService';
import { getErrorMessage } from '../../../../services/errorService';

interface ListaPrecioState {
  listas: ListaPrecio[];
  selectedLista: ListaPrecio | null;
  loading: boolean;
  error: string | null;
  dialogOpen: boolean;

  fetchListas: () => Promise<void>;
  fetchListaById: (id: number) => Promise<void>;
  saveLista: (data: ListaPrecio) => Promise<void>;
  deleteLista: (id: number) => Promise<void>;
  setSelectedLista: (lista: ListaPrecio | null) => void;
  openDialog: () => void;
  closeDialog: () => void;
  clearError: () => void;
  reset: () => void;
}

const initialState = {
  listas: [],
  selectedLista: null,
  loading: false,
  error: null,
  dialogOpen: false,
};

export const useListaPrecioStore = create<ListaPrecioState>()(
  devtools(
    (set) => ({
      ...initialState,

      fetchListas: async () => {
        set({ loading: true, error: null }, false, 'listaPrecio/fetchAll/start');
        try {
          const listas = await ListaPrecioService.getAll();
          set({ listas, loading: false }, false, 'listaPrecio/fetchAll/success');
        } catch (error) {
          set({ loading: false, error: getErrorMessage(error, 'Error al cargar listas') }, false, 'listaPrecio/fetchAll/error');
        }
      },

      fetchListaById: async (id: number) => {
        set({ loading: true, error: null }, false, 'listaPrecio/fetchById/start');
        try {
          const lista = await ListaPrecioService.getById(id);
          set({ selectedLista: lista, loading: false }, false, 'listaPrecio/fetchById/success');
        } catch (error) {
          set({ loading: false, error: getErrorMessage(error, 'Error al cargar lista') }, false, 'listaPrecio/fetchById/error');
        }
      },

      saveLista: async (data: ListaPrecio) => {
        set({ loading: true, error: null }, false, 'listaPrecio/save/start');
        try {
          await ListaPrecioService.save(data);
          set({ loading: false, dialogOpen: false }, false, 'listaPrecio/save/success');
        } catch (error) {
          set({ loading: false, error: getErrorMessage(error, 'Error al guardar lista') }, false, 'listaPrecio/save/error');
          throw error;
        }
      },

      deleteLista: async (id: number) => {
        set({ loading: true, error: null }, false, 'listaPrecio/delete/start');
        try {
          await ListaPrecioService.delete(id);
          set((state) => ({
            listas: state.listas.filter((l) => l.idListaPrecio !== id),
            loading: false,
          }), false, 'listaPrecio/delete/success');
        } catch (error) {
          set({ loading: false, error: getErrorMessage(error, 'Error al eliminar lista') }, false, 'listaPrecio/delete/error');
        }
      },

      setSelectedLista: (lista) => set({ selectedLista: lista }, false, 'listaPrecio/setSelected'),
      openDialog: () => set({ dialogOpen: true }, false, 'listaPrecio/openDialog'),
      closeDialog: () => set({ dialogOpen: false, selectedLista: null }, false, 'listaPrecio/closeDialog'),
      clearError: () => set({ error: null }, false, 'listaPrecio/clearError'),
      reset: () => set(initialState, false, 'listaPrecio/reset'),
    }),
    { name: 'lista-precio-store' }
  )
);
